import type { AbilitySlot, HeroId } from '@moba/shared';

import { LANG } from './i18n.ts';
/** Casilla de la barra de habilidades: el ataque básico y las cuatro habilidades. */
export type KitSlot = 'attack' | AbilitySlot;

/** Nombre de cada habilidad (HUD y pantalla de selección). */
const KIT_NAMES_ES: Record<HeroId, Record<KitSlot, string>> = {
  knight: { attack: 'Combo', q: 'Arco', w: 'Giro de espada', e: 'Bomba (E de nuevo: detonar)', r: 'Juicio de la Trifuerza' },
  barbarian: { attack: 'Combo', q: 'Aliento de fuego', w: 'Caparazón de púas', e: 'Golpe de tierra', r: 'Agarre aéreo' },
  mage: { attack: 'Combo', q: 'Disparo de carga', w: 'Misil', e: 'Bomba', r: 'Screw Attack' },
  falco: { attack: 'Disparo', q: 'Blaster', w: 'Phantasm', e: 'Reflector', r: 'Fire Bird' },
  mario: { attack: 'Combo', q: 'Capa', w: 'Super Jump Punch', e: 'Bola de fuego', r: 'Tornado' },
  sheik: { attack: 'Combo', q: 'Agujas', w: 'Vanish', e: 'Cadena', r: 'Ráfaga' },
};

/** Descripción corta de lo que hace cada habilidad (tooltip). */
const KIT_DESCRIPTIONS_ES: Record<HeroId, Partial<Record<KitSlot, string>>> = {
  knight: {
    q: 'Tensa el arco y dispara una flecha en línea recta. Tiene 3 flechas: con alguna en el carcaj recupera una cada tercio del enfriamiento; si gasta las 3, entra en el enfriamiento completo.',
    w: 'Gira con la espada golpeando alrededor; puede caminar mientras gira.',
    e: 'Lanza una bomba que explota al final; pulsa E otra vez para detonarla antes.',
    r: 'Marca con la Trifuerza a un héroe enemigo cercano (el fijado o el más cercano al cursor) y hace caer sobre él una espada gigante: daño puro que crece con la vida que le falta. Ideal para rematar.'
  },
  barbarian: {
    q: 'Escupe llamas en un cono largo durante más de 2 s; puede girar sobre sí mismo hacia el cursor mientras lo hace.',
    w: 'Se envuelve 4 s en una burbuja de espinas: absorbe 40 de daño y devuelve el 10% del daño recibido.',
    e: 'Golpea el suelo y aturde 1 s a todos los enemigos de un área grande a su alrededor.',
    r: 'Embiste y agarra a un héroe enemigo, salta con él por los aires y lo estrella contra el suelo (aturde y daña alrededor).',
  },
  mage: {
    q: 'Dispara una esfera de energía cargada.',
    w: 'Lanza un misil que explota al impactar.',
    e: 'Suelta una bomba lenta que explota en área.',
    r: 'Gira envuelta en energía y atraviesa a los rivales.',
  },
  falco: {
    attack: 'Ataque a distancia con el blaster.',
    q: 'Ráfaga doble del blaster de largo alcance.',
    w: 'Se lanza como un rayo y golpea a todo lo que atraviesa.',
    e: 'Reflector: devuelve los proyectiles enemigos y empuja.',
    r: 'Se convierte en un ave de fuego imparable y carga en línea recta.',
  },
  mario: {
    q: 'Barrido amplio con la capa.',
    w: 'Salta con un gancho hacia arriba soltando monedas.',
    e: 'Bola de fuego que va botando por el suelo.',
    r: 'Gira como un tornado golpeando varias veces.',
  },
  sheik: {
    q: 'Lanza tres agujas en abanico.',
    w: 'Desaparece en una nube y reaparece más adelante.',
    e: 'Latigazo con la cadena que aturde.',
    r: 'Ráfaga de golpes alrededor.',
  },
};

const KIT_NAMES_EN: Record<HeroId, Record<KitSlot, string>> = {
  knight: { attack: 'Combo', q: 'Bow', w: 'Spin Attack', e: 'Bomb (E again: detonate)', r: 'Judgment of the Triforce' },
  barbarian: { attack: 'Combo', q: 'Fire Breath', w: 'Spiked Shell', e: 'Ground Pound', r: 'Flying Slam' },
  mage: { attack: 'Combo', q: 'Charge Shot', w: 'Missile', e: 'Bomb', r: 'Screw Attack' },
  falco: { attack: 'Shot', q: 'Blaster', w: 'Phantasm', e: 'Reflector', r: 'Fire Bird' },
  mario: { attack: 'Combo', q: 'Cape', w: 'Super Jump Punch', e: 'Fireball', r: 'Tornado' },
  sheik: { attack: 'Combo', q: 'Needles', w: 'Vanish', e: 'Chain', r: 'Flurry' },
};

const KIT_DESCRIPTIONS_EN: Record<HeroId, Partial<Record<KitSlot, string>>> = {
  knight: {
    q: 'Draws the bow and fires an arrow in a straight line. Holds 3 arrows: while any is left it regains one every third of the cooldown; spending all 3 starts the full cooldown.',
    w: 'Spins with the sword hitting everything around; can walk while spinning.',
    e: 'Throws a bomb that explodes at the end; press E again to detonate it early.',
    r: 'Marks a nearby enemy hero with the Triforce (the locked one or the closest to the cursor) and drops a giant sword on them: true damage that grows with their missing health. Ideal for finishing.',
  },
  barbarian: {
    q: 'Breathes fire in a long cone for over 2 s; can turn toward the cursor while doing it.',
    w: 'Wraps himself in a spiked bubble for 4 s: absorbs 40 damage and reflects 10% of the damage taken.',
    e: 'Slams the ground and stuns every enemy in a large area around him for 1 s.',
    r: 'Charges and grabs an enemy hero, leaps into the air with them and slams them into the ground (stuns and damages around).',
  },
  mage: {
    q: 'Fires a charged energy ball.',
    w: 'Launches a missile that explodes on impact.',
    e: 'Drops a slow bomb that explodes in an area.',
    r: 'Spins wrapped in energy and cuts through enemies.',
  },
  falco: {
    attack: 'Ranged attack with the blaster.',
    q: 'Long-range double blaster burst.',
    w: 'Dashes like lightning and hits everything in the way.',
    e: 'Reflector: sends enemy projectiles back and pushes.',
    r: 'Becomes an unstoppable fire bird and charges in a straight line.',
  },
  mario: {
    q: 'Wide sweep with the cape.',
    w: 'Jumps with an upward punch, popping out coins.',
    e: 'Fireball that bounces along the ground.',
    r: 'Spins like a tornado, hitting several times.',
  },
  sheik: {
    q: 'Throws three needles in a fan.',
    w: 'Vanishes in a cloud and reappears further ahead.',
    e: 'Chain lash that stuns.',
    r: 'Flurry of strikes all around.',
  },
};

/** Nombres y descripciones en el idioma de la interfaz (fijo durante la sesión). */
export const KIT_NAMES = LANG === 'es' ? KIT_NAMES_ES : KIT_NAMES_EN;
export const KIT_DESCRIPTIONS = LANG === 'es' ? KIT_DESCRIPTIONS_ES : KIT_DESCRIPTIONS_EN;

const THEMES: Record<HeroId, [string, string]> = {
  knight: ['#3f9a4c', '#0c2412'],
  barbarian: ['#c4561f', '#2c0d05'],
  mage: ['#c9771f', '#24140a'],
  falco: ['#3a74c9', '#0a1631'],
  mario: ['#c73232', '#2a0808'],
  sheik: ['#6a4cb5', '#140c2c'],
};

function svg(heroId: HeroId, body: string, defs = ''): string {
  const [inner, outer] = THEMES[heroId];
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<defs><radialGradient id="bg" cx="50%" cy="38%" r="75%"><stop offset="0" stop-color="${inner}"/>` +
    `<stop offset="1" stop-color="${outer}"/></radialGradient>` +
    `<filter id="glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="1.6" result="b"/>` +
    `<feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>${defs}</defs>` +
    `<rect width="64" height="64" fill="url(#bg)"/>${body}</svg>`
  );
}

/** Espada recta (punta hacia arriba antes de girar) centrada en (x, y). */
function sword(x: number, y: number, angle: number, scale = 1, blade = '#e3ecf5', guard = '#4a6fd1'): string {
  return (
    `<g transform="translate(${x} ${y}) rotate(${angle}) scale(${scale})">` +
    `<path d="M0 -24 L4 -18 L4 4 L-4 4 L-4 -18 Z" fill="${blade}" stroke="#6f819a" stroke-width="1.2"/>` +
    `<path d="M0 -20 V2" stroke="#ffffff" stroke-opacity="0.6" stroke-width="1"/>` +
    `<rect x="-11" y="4" width="22" height="4.5" rx="2.2" fill="${guard}" stroke="#1d2d5c" stroke-width="0.8"/>` +
    `<rect x="-2.2" y="8.5" width="4.4" height="10" fill="#3b2a5e"/>` +
    `<circle cx="0" cy="20.5" r="3" fill="#f2c94c"/></g>`
  );
}

/** Llama en forma de gota, con la punta en (x, y - h). */
function flame(x: number, y: number, w: number, h: number, outer: string, inner: string): string {
  return (
    `<path d="M${x} ${y - h} C${x + w * 0.9} ${y - h * 0.45} ${x + w} ${y} ${x} ${y} C${x - w} ${y} ${x - w * 0.9} ${y - h * 0.45} ${x} ${y - h} Z" fill="${outer}"/>` +
    `<path d="M${x} ${y - h * 0.6} C${x + w * 0.5} ${y - h * 0.3} ${x + w * 0.55} ${y} ${x} ${y} C${x - w * 0.55} ${y} ${x - w * 0.5} ${y - h * 0.3} ${x} ${y - h * 0.6} Z" fill="${inner}"/>`
  );
}

/** Estrella de cuatro puntas (destellos). */
function sparkle(x: number, y: number, r: number, color = '#ffffff'): string {
  const k = r * 0.25;
  return `<path d="M${x} ${y - r} L${x + k} ${y - k} L${x + r} ${y} L${x + k} ${y + k} L${x} ${y + r} L${x - k} ${y + k} L${x - r} ${y} L${x - k} ${y - k} Z" fill="${color}"/>`;
}

/** Líneas de velocidad horizontales a la izquierda de x. */
function speedLines(x: number, ys: number[], length: number, color = '#ffffff'): string {
  return ys
    .map((y, i) => `<line x1="${x - length + i * 3}" y1="${y}" x2="${x}" y2="${y}" stroke="${color}" stroke-opacity="0.55" stroke-width="2" stroke-linecap="round"/>`)
    .join('');
}

/** Corte curvo blanco con estela (zarpazos, tajos). */
function slash(d: string, width = 3.2, color = '#ffffff'): string {
  return `<path d="${d}" stroke="${color}" stroke-width="${width}" fill="none" stroke-linecap="round" filter="url(#glow)"/>`;
}

function spikesAround(cx: number, cy: number, r: number, count: number, length: number, color: string): string {
  let out = '';
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    const b = 0.22;
    const p1 = [cx + Math.cos(a - b) * r, cy + Math.sin(a - b) * r];
    const p2 = [cx + Math.cos(a + b) * r, cy + Math.sin(a + b) * r];
    const tip = [cx + Math.cos(a) * (r + length), cy + Math.sin(a) * (r + length)];
    out += `<path d="M${p1[0].toFixed(1)} ${p1[1].toFixed(1)} L${tip[0].toFixed(1)} ${tip[1].toFixed(1)} L${p2[0].toFixed(1)} ${p2[1].toFixed(1)} Z" fill="${color}" stroke="#8a7a55" stroke-width="0.6"/>`;
  }
  return out;
}

const ICONS: Record<HeroId, Record<KitSlot, () => string>> = {
  knight: {
    attack: () =>
      svg('knight', slash('M10 44 Q22 10 54 14', 2.4, '#d8fff0') + sword(32, 30, 40, 0.95)),
    q: () =>
      svg(
        'knight',
        `<path d="M24 7 Q48 28 24 49" stroke="#8b5a2b" stroke-width="4.5" fill="none" stroke-linecap="round"/>` +
          `<path d="M24 7 Q48 28 24 49" stroke="#c48a4a" stroke-width="1.5" fill="none" stroke-linecap="round"/>` +
          `<polyline points="24,7 13,28 24,49" stroke="#f1ecd8" stroke-width="1.2" fill="none"/>` +
          `<line x1="13" y1="28" x2="52" y2="28" stroke="#e2cf9c" stroke-width="2.4"/>` +
          `<path d="M57 28 L47 23 L49 28 L47 33 Z" fill="#d6e0ea" stroke="#7d8fa6" stroke-width="0.8"/>` +
          `<path d="M13 28 L8 23 M13 28 L8 33 M16 28 L11 23 M16 28 L11 33" stroke="#e05a5a" stroke-width="2" stroke-linecap="round"/>`,
      ),
    w: () =>
      svg(
        'knight',
        `<ellipse cx="32" cy="28" rx="24" ry="13" stroke="#9fefff" stroke-width="2.6" fill="none" stroke-dasharray="30 8" filter="url(#glow)"/>` +
          `<path d="M54 22 l3 7 l-7 -1 z" fill="#9fefff"/><path d="M10 34 l-3 -7 l7 1 z" fill="#9fefff"/>` +
          sword(32, 28, 90, 0.9),
      ),
    e: () =>
      svg(
        'knight',
        `<ellipse cx="30" cy="48" rx="15" ry="3" fill="#000" opacity="0.35"/>` +
          `<circle cx="30" cy="32" r="15" fill="#25327e" stroke="#0f1640" stroke-width="1.5"/>` +
          `<circle cx="24" cy="26" r="4.5" fill="#ffffff" opacity="0.35"/>` +
          `<rect x="35" y="14" width="8" height="6" rx="1.5" transform="rotate(35 39 17)" fill="#9aa6b8"/>` +
          `<path d="M42 14 Q46 8 50 10" stroke="#c8a45a" stroke-width="2" fill="none"/>` +
          sparkle(51, 9, 6, '#ffd84a') +
          sparkle(51, 9, 3, '#ffffff'),
      ),
    r: () => {
      // Trifuerza dorada con la espada cayendo en vertical
      const tri = (x1: number, y1: number, x2: number, y2: number, x3: number, y3: number) =>
        `<path d="M${x1} ${y1} L${x2} ${y2} L${x3} ${y3} Z" fill="url(#gold)" stroke="#8a5a00" stroke-width="0.8"/>`;
      return svg(
        'knight',
        `<circle cx="32" cy="22" r="18" fill="#ffd25a" opacity="0.18" filter="url(#glow)"/>` +
          tri(32, 6, 23.5, 20.5, 40.5, 20.5) +
          tri(23, 21.5, 14.5, 36, 31.5, 36) +
          tri(41, 21.5, 32.5, 36, 49.5, 36) +
          sword(32, 38, 180, 0.72) +
          `<ellipse cx="32" cy="57" rx="16" ry="3.2" stroke="#ffe08a" stroke-width="1.8" fill="none" opacity="0.85"/>` +
          sparkle(52, 10, 5, '#fff3c0') +
          sparkle(12, 14, 3.5, '#fff3c0'),
        `<linearGradient id="gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff0a8"/><stop offset="1" stop-color="#e3a21a"/></linearGradient>`,
      );
    },
  },
  barbarian: {
    attack: () =>
      svg(
        'barbarian',
        slash('M18 10 Q30 28 22 50') + slash('M30 8 Q42 28 34 50') + slash('M42 10 Q54 28 46 50') +
          `<path d="M18 10 Q30 28 22 50 M30 8 Q42 28 34 50 M42 10 Q54 28 46 50" stroke="#ff9a4a" stroke-width="1" fill="none"/>`,
      ),
    q: () =>
      svg(
        'barbarian',
        `<path d="M8 30 L54 12 Q64 30 54 48 Z" fill="url(#fire)" opacity="0.95"/>` +
          flame(44, 22, 6, 10, '#ffcf4a', '#fff3b0') +
          flame(52, 34, 7, 12, '#ff9a2a', '#ffe07a') +
          flame(40, 44, 5, 9, '#ffcf4a', '#fff3b0') +
          `<path d="M4 24 Q12 30 4 36" stroke="#3d7a2e" stroke-width="5" fill="none" stroke-linecap="round"/>` +
          `<path d="M7 26 L11 28 M7 34 L11 32" stroke="#ffffff" stroke-width="1.5"/>` +
          // gira sobre sí mismo mientras escupe
          `<path d="M10 50 A10 6 0 1 0 6 42" stroke="#ffffff" stroke-width="2" fill="none" stroke-linecap="round"/>` +
          `<path d="M3 44 L6 38 L10 44 Z" fill="#ffffff"/>`,
        `<linearGradient id="fire" x1="0" x2="1"><stop offset="0" stop-color="#fff2a0"/><stop offset="0.45" stop-color="#ffaa2a"/><stop offset="1" stop-color="#d4321a"/></linearGradient>`,
      ),
    w: () =>
      svg(
        'barbarian',
        `<circle cx="32" cy="28" r="23" fill="#ff8a2a" fill-opacity="0.16" stroke="#ffc46a" stroke-width="2" filter="url(#glow)"/>` +
          spikesAround(32, 28, 12, 10, 8, '#f3ead2') +
          `<circle cx="32" cy="28" r="12" fill="#2f8a3a" stroke="#f3ead2" stroke-width="2"/>` +
          `<path d="M32 19 L39 23 L39 32 L32 37 L25 32 L25 23 Z" fill="none" stroke="#1d5a26" stroke-width="1.6"/>` +
          `<path d="M50 9 l4 -3 M54 12 l5 -1 M10 47 l-4 3 M14 50 l-1 5" stroke="#ffe08a" stroke-width="2" stroke-linecap="round"/>`,
      ),
    e: () =>
      svg(
        'barbarian',
        `<ellipse cx="32" cy="46" rx="26" ry="6" stroke="#ffcf7a" stroke-width="2" fill="none" opacity="0.8"/>` +
          `<path d="M32 46 L22 54 M32 46 L44 55 M32 46 L12 46 M32 46 L52 45" stroke="#2a1006" stroke-width="2.4" stroke-linecap="round"/>` +
          `<path d="M14 26 Q32 4 50 26 Z" fill="#2f8a3a" stroke="#f3ead2" stroke-width="2"/>` +
          `<path d="M24 24 L28 14 M32 24 V11 M40 24 L36 14" stroke="#1d5a26" stroke-width="1.8"/>` +
          `<rect x="12" y="25" width="40" height="5" rx="2.5" fill="#f1d9a0"/>` +
          `<path d="M18 36 V42 M32 33 V40 M46 36 V42" stroke="#ffffff" stroke-opacity="0.7" stroke-width="2" stroke-linecap="round"/>` +
          // aturdimiento: estrellas girando
          sparkle(12, 10, 5, '#ffe14a') +
          sparkle(52, 8, 5, '#ffe14a') +
          sparkle(32, 5, 3.5, '#fff4a8'),
      ),
    r: () =>
      svg(
        'barbarian',
        // trayectoria: sube agarrando y cae en picado sobre el suelo
        `<path d="M8 46 Q14 6 34 8 Q48 9 47 40" stroke="#ffe08a" stroke-width="2.2" fill="none" stroke-dasharray="3 3"/>` +
          `<path d="M47 44 L42 34 L52 34 Z" fill="#ffe08a"/>` +
          `<ellipse cx="47" cy="49" rx="13" ry="3.5" fill="none" stroke="#ffffff" stroke-width="2"/>` +
          `<path d="M47 49 L36 55 M47 49 L58 55 M47 49 L33 48 M47 49 L61 48" stroke="#2a1006" stroke-width="2" stroke-linecap="round"/>` +
          sparkle(47, 47, 9, '#ffcf7a') +
          // garra agarrando a la presa en lo alto
          `<circle cx="27" cy="12" r="5" fill="#9cb0c8" stroke="#3a4656" stroke-width="1"/>` +
          `<path d="M18 20 Q18 30 26 30 Q33 30 33 23 L33 18 Z" fill="#e8a33a" stroke="#7a3d10" stroke-width="1.2"/>` +
          `<path d="M20 18 Q20 10 23 8 Q23 13 24 18 M26 17 Q27 9 31 8 Q29 13 30 18" fill="#f3ead2" stroke="#8a7a55" stroke-width="0.8"/>`,
      ),
  },
  mage: {
    attack: () =>
      svg(
        'mage',
        `<rect x="6" y="22" width="26" height="14" rx="6" fill="#e07a1f" stroke="#7a3a08" stroke-width="1.5"/>` +
          `<rect x="24" y="20" width="9" height="18" rx="3" fill="#3fbf6a" stroke="#1d6a36" stroke-width="1.2"/>` +
          `<circle cx="44" cy="29" r="9" fill="#fff0a0" filter="url(#glow)"/>` +
          sparkle(44, 29, 14, '#ffd84a') +
          sparkle(44, 29, 7, '#ffffff'),
      ),
    q: () =>
      svg(
        'mage',
        `<circle cx="32" cy="28" r="22" fill="none" stroke="#7af0ff" stroke-width="1.4" opacity="0.55"/>` +
          `<circle cx="32" cy="28" r="17" fill="none" stroke="#bff8ff" stroke-width="1.4" opacity="0.7" stroke-dasharray="6 4"/>` +
          `<circle cx="32" cy="28" r="12" fill="url(#orb)" filter="url(#glow)"/>`,
        `<radialGradient id="orb"><stop offset="0" stop-color="#ffffff"/><stop offset="0.45" stop-color="#fff59a"/><stop offset="1" stop-color="#ff9a1f"/></radialGradient>`,
      ),
    w: () =>
      svg(
        'mage',
        `<g transform="rotate(-35 32 28)">` +
          flame(10, 28, 5, 12, '#ff7a1a', '#ffe07a').replace(/<path/g, '<path transform="rotate(-90 10 28)"') +
          `<path d="M14 23 H44 L54 28 L44 33 H14 Z" fill="#c9d2dc" stroke="#5a6676" stroke-width="1.2"/>` +
          `<path d="M44 23 L54 28 L44 33 Z" fill="#d13a2a"/>` +
          `<path d="M16 23 L10 17 H18 L22 23 M16 33 L10 39 H18 L22 33" fill="#d13a2a"/>` +
          `<rect x="28" y="23" width="3" height="10" fill="#5a6676"/></g>`,
      ),
    e: () =>
      svg(
        'mage',
        `<ellipse cx="32" cy="42" rx="22" ry="5" fill="none" stroke="#ffd84a" stroke-width="1.6" opacity="0.6"/>` +
          `<ellipse cx="32" cy="42" rx="13" ry="3" fill="none" stroke="#fff3a0" stroke-width="1.6" opacity="0.8"/>` +
          `<circle cx="32" cy="34" r="8" fill="url(#bomb)" filter="url(#glow)"/>` +
          sparkle(32, 34, 13, '#fff7c0'),
        `<radialGradient id="bomb"><stop offset="0" stop-color="#ffffff"/><stop offset="0.5" stop-color="#ffe066"/><stop offset="1" stop-color="#ff7a1a"/></radialGradient>`,
      ),
    r: () =>
      svg(
        'mage',
        `<g filter="url(#glow)" stroke="#c8ff5a" stroke-width="2.2" fill="none">` +
          `<ellipse cx="32" cy="28" rx="22" ry="9" transform="rotate(25 32 28)"/>` +
          `<ellipse cx="32" cy="28" rx="22" ry="9" transform="rotate(-25 32 28)"/>` +
          `<ellipse cx="32" cy="28" rx="9" ry="22" opacity="0.7"/></g>` +
          `<circle cx="32" cy="28" r="7" fill="#e07a1f" stroke="#ffe08a" stroke-width="1.5"/>` +
          `<path d="M50 8 L46 14 L51 15 L47 22" stroke="#ffffff" stroke-width="1.6" fill="none"/>` +
          `<path d="M12 40 L16 46 L11 47 L15 54" stroke="#ffffff" stroke-width="1.6" fill="none"/>`,
      ),
  },
  falco: {
    attack: () =>
      svg(
        'falco',
        `<g transform="rotate(-20 32 28)">` +
          `<rect x="8" y="25" width="44" height="6" rx="3" fill="#ff4a6a" filter="url(#glow)"/>` +
          `<rect x="12" y="27" width="38" height="2" rx="1" fill="#ffe0e8"/></g>` +
          sparkle(52, 21, 6, '#ffffff'),
      ),
    q: () =>
      svg(
        'falco',
        `<path d="M8 30 H26 L28 26 H34 V34 H28 L24 44 H17 L20 34 H8 Z" fill="#c6ced8" stroke="#4a5666" stroke-width="1.2"/>` +
          `<rect x="34" y="28" width="5" height="4" fill="#4a5666"/>` +
          `<rect x="40" y="19" width="20" height="4" rx="2" fill="#ff4a6a" filter="url(#glow)"/>` +
          `<rect x="40" y="33" width="20" height="4" rx="2" fill="#ff4a6a" filter="url(#glow)"/>` +
          `<rect x="42" y="20" width="16" height="1.6" fill="#ffe0e8"/><rect x="42" y="34" width="16" height="1.6" fill="#ffe0e8"/>`,
      ),
    w: () =>
      svg(
        'falco',
        `<path d="M8 18 L22 28 L8 38" stroke="#7ab8ff" stroke-width="4" fill="none" opacity="0.3"/>` +
          `<path d="M18 16 L34 28 L18 40" stroke="#9cccff" stroke-width="4.5" fill="none" opacity="0.55"/>` +
          `<path d="M30 14 L50 28 L30 42 L36 28 Z" fill="#dff0ff" stroke="#ffffff" stroke-width="1.5" filter="url(#glow)"/>` +
          speedLines(28, [10, 46], 20, '#bfe0ff'),
      ),
    e: () =>
      svg(
        'falco',
        `<path d="M32 6 L51 17 L51 39 L32 50 L13 39 L13 17 Z" fill="#ffffff" fill-opacity="0.35" stroke="#6ab8ff" stroke-width="3" filter="url(#glow)"/>` +
          `<path d="M32 13 L45 21 L45 35 L32 43 L19 35 L19 21 Z" fill="#ffffff" fill-opacity="0.25"/>` +
          sparkle(32, 28, 9, '#cfe8ff') +
          sparkle(50, 8, 4, '#9cd0ff') +
          sparkle(12, 48, 3.5, '#9cd0ff'),
      ),
    r: () =>
      svg(
        'falco',
        `<path d="M6 34 Q16 30 22 32 Q12 40 6 34 Z" fill="#ff7a1a" opacity="0.8"/>` +
          `<path d="M4 26 Q16 24 22 28 Q12 30 4 26 Z" fill="#ffcf4a" opacity="0.8"/>` +
          `<path d="M20 30 Q30 8 44 10 Q38 18 40 24 L58 28 L40 32 Q38 38 44 46 Q30 48 20 30 Z" fill="url(#bird)" stroke="#fff3b0" stroke-width="1.2" filter="url(#glow)"/>` +
          `<circle cx="47" cy="27" r="1.6" fill="#7a1a08"/>`,
        `<linearGradient id="bird" x1="0" x2="1"><stop offset="0" stop-color="#d4321a"/><stop offset="0.5" stop-color="#ff8a1a"/><stop offset="1" stop-color="#fff0a0"/></linearGradient>`,
      ),
  },
  mario: {
    attack: () =>
      svg(
        'mario',
        `<path d="M44 14 L50 8 M50 24 L58 22 M46 36 L52 42" stroke="#ffe08a" stroke-width="2.4" stroke-linecap="round"/>` +
          `<rect x="14" y="16" width="26" height="24" rx="9" fill="#ffffff" stroke="#9aa6b8" stroke-width="1.5"/>` +
          `<path d="M22 16 V26 M30 16 V26" stroke="#9aa6b8" stroke-width="1.2"/>` +
          `<rect x="6" y="22" width="10" height="13" rx="3" fill="#ffffff" stroke="#9aa6b8" stroke-width="1.5"/>` +
          `<path d="M16 34 Q22 38 28 34" stroke="#9aa6b8" stroke-width="1.2" fill="none"/>`,
      ),
    q: () =>
      svg(
        'mario',
        `<path d="M14 8 Q8 28 18 48 Q34 40 54 46 Q40 30 46 10 Q30 16 14 8 Z" fill="#d42a2a" stroke="#ffd84a" stroke-width="2.5"/>` +
          `<path d="M20 14 Q16 28 22 42" stroke="#8a1414" stroke-width="2" fill="none"/>` +
          `<path d="M30 16 Q28 30 34 40" stroke="#8a1414" stroke-width="2" fill="none"/>` +
          slash('M52 6 Q62 26 50 50', 2, '#ffffff'),
      ),
    w: () =>
      svg(
        'mario',
        `<path d="M16 50 V36 M24 52 V40 M40 52 V40 M48 50 V36" stroke="#ffffff" stroke-opacity="0.45" stroke-width="2" stroke-linecap="round"/>` +
          `<rect x="22" y="14" width="20" height="22" rx="8" fill="#ffffff" stroke="#9aa6b8" stroke-width="1.5"/>` +
          `<path d="M28 14 V22 M35 14 V22" stroke="#9aa6b8" stroke-width="1.2"/>` +
          `<rect x="24" y="34" width="16" height="10" rx="2" fill="#d42a2a"/>` +
          `<ellipse cx="50" cy="12" rx="6" ry="8" fill="#ffd23a" stroke="#b8860b" stroke-width="1.5"/>` +
          `<rect x="48.6" y="7" width="2.8" height="10" rx="1" fill="#b8860b"/>`,
      ),
    e: () =>
      svg(
        'mario',
        `<line x1="4" y1="46" x2="60" y2="46" stroke="#000" stroke-opacity="0.3" stroke-width="2"/>` +
          `<path d="M6 46 Q13 24 20 46 Q27 30 34 46" stroke="#ffd84a" stroke-width="2.2" fill="none" stroke-dasharray="3 3"/>` +
          `<path d="M36 40 Q38 28 46 22" stroke="#ff9a2a" stroke-width="5" fill="none" stroke-linecap="round" opacity="0.5"/>` +
          `<circle cx="45" cy="28" r="12" fill="url(#fb)" filter="url(#glow)"/>` +
          `<path d="M37 24 Q44 16 54 21 Q48 20 45 25 Z" fill="#fff3b0"/>`,
        `<radialGradient id="fb" cx="40%" cy="40%"><stop offset="0" stop-color="#fff6c0"/><stop offset="0.5" stop-color="#ffb02a"/><stop offset="1" stop-color="#d4321a"/></radialGradient>`,
      ),
    r: () =>
      svg(
        'mario',
        `<g stroke-linecap="round" fill="none">` +
          `<path d="M10 10 Q32 16 54 10" stroke="#ffffff" stroke-width="3"/>` +
          `<path d="M14 18 Q32 24 50 18" stroke="#ff6a6a" stroke-width="3"/>` +
          `<path d="M18 26 Q32 31 46 26" stroke="#ffffff" stroke-width="3"/>` +
          `<path d="M22 33 Q32 37 42 33" stroke="#ff6a6a" stroke-width="3"/>` +
          `<path d="M26 40 Q32 43 38 40" stroke="#ffffff" stroke-width="3"/>` +
          `<path d="M29 46 Q32 48 35 46" stroke="#ff6a6a" stroke-width="3"/></g>` +
          `<path d="M54 10 l4 4 M10 10 l-4 4" stroke="#ffe08a" stroke-width="2" stroke-linecap="round"/>`,
      ),
  },
  sheik: {
    attack: () =>
      svg(
        'sheik',
        slash('M12 40 L40 12', 3) + slash('M22 46 L50 18', 3) +
          sparkle(46, 14, 8, '#e8dcff') +
          sparkle(46, 14, 4, '#ffffff'),
      ),
    q: () =>
      svg(
        'sheik',
        [0, 9, 18]
          .map(
            (dy) =>
              `<g transform="translate(0 ${dy - 9}) rotate(-25 32 28)">` +
              `<line x1="10" y1="28" x2="52" y2="28" stroke="#dfe6ef" stroke-width="2"/>` +
              `<path d="M52 28 L58 28" stroke="#ffffff" stroke-width="1.2"/>` +
              `<line x1="10" y1="28" x2="18" y2="28" stroke="#9a7ad6" stroke-width="3"/></g>`,
          )
          .join('') + sparkle(54, 14, 5, '#ffffff'),
      ),
    w: () =>
      svg(
        'sheik',
        `<g fill="#e6dcff" opacity="0.9">` +
          `<circle cx="22" cy="32" r="9"/><circle cx="34" cy="26" r="11"/><circle cx="44" cy="34" r="8"/><circle cx="30" cy="38" r="8"/></g>` +
          `<g fill="#b9a6ee" opacity="0.8"><circle cx="26" cy="36" r="5"/><circle cx="38" cy="32" r="6"/></g>` +
          sparkle(50, 12, 7, '#ffffff') +
          sparkle(14, 16, 4, '#d8ccff') +
          `<path d="M52 46 L58 46 M54 50 L60 50" stroke="#ffffff" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round"/>`,
      ),
    e: () => {
      let links = '';
      for (let i = 0; i < 6; i++) {
        const t = i / 5;
        const x = 10 + t * 38;
        const y = 44 - Math.sin(t * Math.PI * 0.9) * 26;
        links += `<ellipse cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="4.6" ry="2.6" transform="rotate(${-50 + t * 90} ${x.toFixed(1)} ${y.toFixed(1)})" fill="none" stroke="#cfd8e3" stroke-width="2"/>`;
      }
      return svg(
        'sheik',
        links + `<path d="M50 26 L58 20 L56 30 L62 34 L52 34 Z" fill="#dfe6ef" stroke="#6f819a" stroke-width="1"/>` + sparkle(56, 12, 5, '#ffe9a0'),
      );
    },
    r: () => {
      let cuts = '';
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + 0.3;
        const x1 = 32 + Math.cos(a) * 8;
        const y1 = 28 + Math.sin(a) * 8;
        const x2 = 32 + Math.cos(a + 0.35) * 24;
        const y2 = 28 + Math.sin(a + 0.35) * 24;
        cuts += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#ffffff" stroke-width="2.6" stroke-linecap="round" filter="url(#glow)"/>`;
      }
      return svg('sheik', cuts + sparkle(32, 28, 9, '#e8dcff') + sparkle(32, 28, 4, '#ffffff'));
    },
  },
};

const cache = new Map<string, string>();

/** URL `data:` con la ilustración SVG de una habilidad. */
export function abilityIcon(heroId: HeroId, slot: KitSlot): string {
  const key = `${heroId}:${slot}`;
  let url = cache.get(key);
  if (!url) {
    url = `data:image/svg+xml,${encodeURIComponent(ICONS[heroId][slot]())}`;
    cache.set(key, url);
  }
  return url;
}
